import { ReactNode, useEffect, useState } from 'react';
import { loginRequest } from '../api/calls';
import { getUser, updateUser } from '../api/user.api';
import { useFirebaseNotifications } from '../hooks/useFirebaseNotifications';
import { AppContextI, UserI } from '../types/user';
import { UserContext } from './user-context';

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: Readonly<UserProviderProps>) {
  const [userInfo, setUserInfo] = useState<UserI | undefined>(undefined);
  const [token, setToken] = useState<string | null>(null);

  // Initialize FCM notifications
  const fcmNotifications = useFirebaseNotifications({
    jwtToken: token,
    onNotificationReceived: (payload) => {
      if (userInfo) {
        window.dispatchEvent(
          new CustomEvent('fcm-notification-received', { detail: payload })
        );
      }
    }
  });

  const userLogin: AppContextI['userLogin'] = async (id, otp, token) => {
    console.log(
      `🚀 ~ file: appContext.js:40 ~ userLogin ~ (id, otp, token):`,
      id,
      otp,
      token
    );

    try {
      const data = await loginRequest(id, otp);
      localStorage.setItem('token', data.accessToken);
      setToken(data.accessToken);
    } catch (err: unknown) {
      const error = new Error(
        err instanceof Error ? err.message : 'Login failed'
      );
      //@ts-expect-error - No check
      error.status =
        err && typeof err === 'object' && 'status' in err
          ? err.status
          : undefined;
      throw error;
    }
  };

  const updateUserInfo: AppContextI['updateUserInfo'] = async (newInfo) => {
    const prevState = userInfo;
    if (!userInfo) return;
    setUserInfo({ ...userInfo, ...newInfo });
    await updateUser({ ...userInfo, ...newInfo }).catch((e) => {
      console.error(e);
      setUserInfo(prevState);
    });
  };

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    console.log(
      `🚀 ~ file: appContext.js:15 ~ useEffect ~ savedToken:`,
      savedToken
    );

    if (savedToken) {
      setToken(savedToken);

      async function fetchData() {
        try {
          const data = await getUser();
          console.debug(
            'ℹ️ ~ file: appContext.js:52 ~ fetchData ~ data:',
            data
          );
          setUserInfo({
            country_code: data.country_code,
            user_birthday: data.user_birthday,
            user_email: data.user_email,
            user_id: data.user_id,
            user_name: data.user_name,
            user_phone_number: data.user_phone_number,
            newsletter: data.newsletter,
            marketing_and_offers: data.marketing_and_offers,
            text_message: data.text_message,
            noPinTransactionLimit: 0
          });
        } catch (error: unknown) {
          if (
            error &&
            typeof error === 'object' &&
            'status' in error &&
            error.status === 401
          ) {
            // localStorage.removeItem('token')
            // localStorage.removeItem('refresh_token')
            setToken(null);
            setUserInfo(undefined);
          } else {
            console.error(
              'Failed to fetch user data:',
              error instanceof Error ? error.message : 'Unknown error'
            );
          }
        }
      }

      fetchData();
    }
  }, []);
  return (
    <UserContext.Provider
      value={{
        userInfo,
        setUserInfo,
        updateUserInfo,
        userLogin,
        token,
        fcm: fcmNotifications
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
